import { ApiError } from "@api/errors";
import { useGenebankAccessions, useGenebankContainers, useGenebankPlots, useGenebankSisters } from "@api/generated/genebank/genebank";
import type {
  Accession,
  AccessionItemIn,
  PlannedItem,
  PlotItemIn,
  QuerySource,
  RecipientView,
  Recorded,
  ShipmentDetail,
  ShipmentIn,
  ShipmentInMailType,
  ShipmentItemsIn,
  ShipmentRow,
  ShippedItem,
} from "@api/generated/model";
import {
  getShipmentsDetailQueryKey,
  getShipmentsDocumentAddressUrl,
  getShipmentsDocumentDeliverySheetUrl,
  getShipmentsDocumentDeliveryUrl,
  getShipmentsDocumentListUrl,
  getShipmentsDocumentNovexxUrl,
  getShipmentsDocumentStickersUrl,
  getShipmentsOptionsQueryKey,
  getShipmentsQuerySourcesQueryKey,
  getShipmentsShipmentsQueryKey,
  shipmentsAddPreview,
  shipmentsPreview,
  shipmentsSendNovexx,
  useShipmentsAddRun,
  useShipmentsDetail,
  useShipmentsDetails,
  useShipmentsOptions,
  useShipmentsQuerySources,
  useShipmentsRecipient,
  useShipmentsRemoveItem,
  useShipmentsRun,
  useShipmentsShipments,
} from "@api/generated/shipments/shipments";
import { useUserMe } from "@api/generated/user/user";
import { Icon } from "@app/icons";
import { usePreview } from "@app/preview";
import { useInvalidate } from "@app/queryClient";
import { useTrial } from "@app/trials";
import { hasRole, useAutosave, useSeasonFromLink, useSettings } from "@app/workspace";
import { SendToPrinter } from "@features/documents/SendToPrinter";
import { LineLink } from "@features/genealogy/GenotypeParts";
import { hint } from "@i18n";
import { QueryFeedback } from "@ui/QueryFeedback";
import "@features/genebank/genebank.css";
import { detailLines, partnerLine } from "@features/genebank/details";
import { kgColumn, PartnerSelect, removeColumn, useGenebankChanged, useKeptViews } from "@features/genebank/parts";
import {
  isEmptySource,
  MAIL_TYPES,
  mailTypeText,
  type PickedPlot,
  plotItems,
  plotKey,
  queryItems,
  subplots,
} from "@features/genebank/shipments";
import { SavedQuerySelect, useSavedQuery } from "@features/queries/SavedQuerySelect";
import { ResultBanner } from "@features/system/undo";
import { plotLabel } from "@features/trials/plotkey";
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Loader,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Tabs,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AnimatedTabsList } from "@ui/AnimatedTabsList";
import { BackButton } from "@ui/BackButton";
import { DateField } from "@ui/DateField";
import { cellText, formatDate, formatNumber, localToday, num, shortTime } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { RunBar } from "@ui/RunBar";
import { SearchBox } from "@ui/SearchBox";
import { Splitter } from "@ui/Splitter";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { ShownOrderButton, useListOrder } from "@ui/table/listOrder";
import { usePicks } from "@ui/table/usePicks";
import { YearInput } from "@ui/YearInput";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// the keys the pickers tick rows by (stable: the pick columns are kept while they are the same)
const accessionId = (a: Accession) => a.id;
const sourceRef = (r: QuerySource) => r.source.ref;
const hasSeed = (r: QuerySource) => !isEmptySource(r);
const pickedPlotKey = (p: PickedPlot) => plotKey(p.plot);
/** A plot the trial picker lists: a sister plot names the plots it was found from. */
type PlotRow = PickedPlot & { origins?: [string, number][] };
const shippedId = (it: ShippedItem) => it.id;

export const Route = createFileRoute("/genebank/shipments")({ component: ShipmentPage });

/** A draft item: the API's item, and how the table shows it until the preview has read the source (the preview's
 *  name wins). */
type DraftItem = (PlotItemIn | AccessionItemIn) & { label: string; name?: string | null; measure: "kg" | "bags" };
/** A recorded shipment the items are added to (the legacy picked an existing Shipment ID). */
type Into = { id: number; institution: string | null; day: string | null; remark?: string | null };

/** A recorded shipment's top part on its items' Excel file and printout: its number, recipient and date, then
 *  ``more``. */
const shipmentLines = (t: TFunction, s: Into, more: Parameters<typeof detailLines>[0] = []) =>
  detailLines([
    [t("shipments.number"), s.id],
    [t("shipments.recipient"), s.institution],
    [t("shipments.date"), formatDate(s.day)],
    [t("shipments.shipmentRemark"), s.remark],
    ...more,
  ]);
type Draft = {
  caz: number | null;
  day: string;
  day_chosen?: boolean; // the date was picked by hand (else a draft opened on a later day takes that day)
  mail_type: ShipmentInMailType;
  items: DraftItem[];
  skip_duplicates: boolean;
  reps: number[]; // the harvest repetitions (Trms I–III) a plot's seed left counts
  chemical: string; // what items added from now on get (the legacy form-wide Treated / Chemical)
  remark: string; // the shipment's own (the parcel's), not an item's
  into: Into | null;
  query: number | null; // the saved query items were last taken from (the legacy Magk with the Query closed on it)
};
const REPS = [1, 2, 3];
const ROMAN = ["I", "II", "III"];
// the shipments list's length: the newest ones (the API's ``SHIPMENT_LIST``)
const LISTED = 300;

const empty = (): Draft => ({
  caz: null,
  day: localToday(),
  mail_type: null,
  items: [],
  skip_duplicates: true,
  reps: REPS,
  chemical: "",
  remark: "",
  into: null,
  query: null,
});
/** The item's source as the preview names it (``source.ref``: "ev/th/kis/parc/alp", an accession's id). */
const refOf = (it: PlotItemIn | AccessionItemIn) =>
  it.kind === "plot" ? `${it.ev}/${it.th}/${it.kis}/${it.parc}/${it.alp ?? 0}` : String(it.id);
/** A stored draft as this page reads it (items saved in an older shape are left out, new fields get defaults): its
 *  date today unless picked by hand, a mail type no longer offered left out. */
const restored = (d: Draft | undefined): Draft | null =>
  d
    ? {
        ...empty(),
        ...d,
        day: d.day_chosen && d.day ? d.day : localToday(),
        mail_type: d.mail_type && MAIL_TYPES.includes(d.mail_type) ? d.mail_type : null,
        items: (d.items ?? []).filter((x) => (x.kind === "plot" ? x.parc != null : x.id != null)),
      }
    : null;

/** What the preview is asked: a new shipment, or items for a recorded one. */
type Asked = { into: null; body: ShipmentIn } | { into: number; body: ShipmentItemsIn };

/** Génbank és magposta → Magküldés (the legacy Magk): a shipment put together from trial plots and gene-bank
 *  accessions (kept as a draft per user), checked, recorded with the seed taken from its sources, its documents; more
 *  items can be added to a recorded shipment the same way. */
function ShipmentPage() {
  const settings = useSettings();
  if (!settings) return <Loader size="sm" m="md" />;
  return <ShipmentEditor stored={restored(settings.shipment_draft?.spec as Draft | undefined)} />;
}

function ShipmentEditor({ stored }: { stored: Draft | null }) {
  const { t } = useTranslation();
  const canEdit = hasRole(useUserMe().data?.roles, "genbank");
  const season = useSeasonFromLink(undefined);
  const client = useQueryClient();
  const genebankChanged = useGenebankChanged();
  const options = useShipmentsOptions().data;
  const [draft, setDraft] = useState<Draft>(stored ?? empty());
  const { savedAt, saveNow } = useAutosave("shipment_draft", { spec: draft }, { spec: stored ?? undefined });
  const [picking, setPicking] = useState<"plot" | "accession" | "query" | null>(null);
  // the previous shipments' window: closed, open on the list (0) or on one shipment
  // the previous shipments' window: closed (undefined), on the list (null) or on a shipment
  const [history, setHistory] = useState<number | null | undefined>(undefined);
  const [recorded, setRecorded] = useState<(Recorded & { added: boolean }) | null>(null);
  const upd = (p: Partial<Draft>) => {
    setRecorded(null); // the next shipment is being put together
    setDraft((d) => ({ ...d, ...p }));
  };
  const setItem = useCallback(
    (i: number, p: Partial<DraftItem>) =>
      setDraft((d) => ({ ...d, items: d.items.map((x, j) => (j === i ? ({ ...x, ...p } as DraftItem) : x)) })),
    [],
  );
  const removeItem = useCallback((i: number) => setDraft((d) => ({ ...d, items: d.items.filter((_, j) => j !== i) })), []);
  // an item without an amount above 0 is not asked about (the server refuses it): the run bar asks for one
  const weighed = draft.items.filter((x) => x.amount > 0);
  const unweighed = draft.items.length - weighed.length;
  const items = weighed.map(({ label: _l, name: _n, measure: _m, ...it }) => it);
  const common = { skip_duplicates: draft.skip_duplicates, reps: draft.reps, items };
  const asked: Asked | null = !items.length
    ? null
    : draft.into
      ? { into: draft.into.id, body: common }
      : draft.caz != null && draft.day
        ? {
            into: null,
            body: {
              ...common,
              caz: draft.caz,
              day: draft.day,
              mail_type: draft.mail_type,
              remark: draft.remark || null,
            },
          }
        : null;
  const preview = usePreview("shipment-preview", asked, (a, signal) =>
    a.into == null ? shipmentsPreview(a.body, { signal }) : shipmentsAddPreview(a.into, a.body, { signal }),
  );
  const plan = preview.data;
  const done = (out: Recorded) => {
    setRecorded({ ...out, added: draft.into != null });
    // items added to a recorded shipment: the page stays on it (more can be added; the banner says where to stop)
    const next = { ...empty(), caz: draft.caz, mail_type: draft.mail_type, reps: draft.reps, query: draft.query, into: draft.into };
    setDraft(next);
    saveNow({ spec: next });
    void client.invalidateQueries({ queryKey: getShipmentsOptionsQueryKey() });
    void client.invalidateQueries({ queryKey: [getShipmentsShipmentsQueryKey()[0]] });
    if (out.id != null) void client.invalidateQueries({ queryKey: getShipmentsDetailQueryKey(out.id) });
    genebankChanged();
  };
  // the shipment items are added to may be gone since (undone in the journal, deleted): back to a new shipment
  const target = useShipmentsDetail(draft.into?.id ?? 0, { query: { enabled: draft.into != null, retry: false } });
  const gone = draft.into != null && target.error instanceof ApiError && target.error.status === 404 ? draft.into.id : null;
  useEffect(() => {
    if (gone == null) return;
    setDraft((d) => ({ ...d, into: null }));
    notifications.show({ color: "yellow", message: t("shipments.targetGone", { id: gone }) });
  }, [gone, t]);
  const run = useShipmentsRun({ mutation: { onSuccess: done } });
  const addRun = useShipmentsAddRun({ mutation: { onSuccess: done } });
  const add = (picked: DraftItem[]) => {
    setRecorded(null);
    const have = new Set(draft.items.map((x) => `${x.kind}:${refOf(x)}`));
    // the default treatment for new items (the legacy form's Treated / Chemical while set)
    const withDefaults = picked.map((x) => ({ ...x, chemical: x.chemical ?? (draft.chemical || null) }));
    upd({ items: [...draft.items, ...withDefaults.filter((x) => !have.has(`${x.kind}:${refOf(x)}`))] });
    setPicking(null);
  };
  const rows = plan?.rows ?? [];
  const kept = rows.filter((r) => !r.checked.skipped);
  const refused = kept.some((r) => r.checked.problem);
  // the items in the draft's order (the shipment's and its documents' order; the table's sort only shows them so); a
  // view stays the same object while its item does (a keystroke redraws only the row typed in)
  const keep = useKeptViews<DraftItem, ItemView>();
  const shown = useMemo(() => {
    const planned = new Map((plan?.rows ?? []).map((r) => [`${r.checked.item.source.kind}:${r.checked.item.source.ref}`, r]));
    return draft.items.map((it, index) => keep(it, { it, index, planned: planned.get(`${it.kind}:${refOf(it)}`) }));
  }, [draft.items, plan, keep]);
  // the items' order is the order recorded (the list, the delivery note, the labels): moved here, or the table's sort kept
  const setItems = useCallback((f: (items: DraftItem[]) => DraftItem[]) => setDraft((d) => ({ ...d, items: f(d.items) })), []);
  const order = useListOrder({ setItems, place: placeOf, itemOf, enabled: canEdit });
  const own = useItemColumns(options?.chemicals ?? NONE, setItem, removeItem);
  const columns = useMemo(() => (order.column ? [order.column, ...own] : own), [own, order.column]);
  const table = useDataTable("genebank.shipments.items", columns, shown);
  const recipient = useShipmentsRecipient(draft.caz ?? 0, { query: { enabled: draft.caz != null && !draft.into } }).data;
  // the top part on the items' Excel file and printout: which shipment, to whom, when (the recipient as its card shows it)
  const about = draft.into
    ? shipmentLines(t, draft.into)
    : detailLines([
        [t("shipments.number"), options?.next_id],
        [t("shipments.recipient"), partnerLine(recipient?.recipient)],
        [t("shipments.mailType"), mailTypeText(t, draft.mail_type)],
        [t("shipments.date"), draft.day && formatDate(draft.day)],
        [t("shipments.shipmentRemark"), draft.remark],
      ]);
  const onRun = () => {
    if (!asked || !plan) return;
    if (asked.into == null) run.mutate({ data: { ...asked.body, token: plan.token } });
    else addRun.mutate({ shipment: asked.into, data: { ...asked.body, token: plan.token } });
  };
  return (
    <div className="page fill">
      <PageHeader
        title={t("shipments.title")}
        subtitle={t("shipments.subtitle")}
        actions={
          <div className="hstack" style={{ gap: 8 }}>
            {draft.into ? (
              <span className="chip b">{t("shipments.addToTitle", { id: draft.into.id })}</span>
            ) : (
              options && <span className="chip y">{t("shipments.draft", { id: options.next_id })}</span>
            )}
            {savedAt && <span className="sm mu">{t("shipments.draftSaved", { time: shortTime(savedAt.toISOString()) })}</span>}
            <Button size="compact-sm" variant="default" onClick={() => setHistory(null)}>
              {t("shipments.previous")}
            </Button>
          </div>
        }
      />
      {draft.into && (
        <section className="result-banner info" role="status" aria-label={t("shipments.addToTitle", { id: draft.into.id })}>
          <Icon name="plus" size={16} />
          <span className="grow stack" style={{ gap: 2 }}>
            <b>
              {t("shipments.addingTo", {
                id: draft.into.id,
                recipient: draft.into.institution ?? "—",
                day: formatDate(draft.into.day),
              })}
            </b>
            <span className="sm">{t("shipments.addingToHelp")}</span>
          </span>
          <Button size="xs" variant="default" onClick={() => setHistory(draft.into?.id ?? null)}>
            {t("shipments.showShipment")}
          </Button>
          <Button size="xs" variant="default" onClick={() => upd({ into: null })}>
            {t("shipments.stopAdding")}
          </Button>
        </section>
      )}
      <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
      <div className="fill-row">
        <Recipient draft={draft} view={recipient} upd={upd} chemicals={options?.chemicals ?? NONE} />
        <Splitter cssVar="--gb-recipient-w" side="start" min={180} max={480} />
        <section className="card stack grow" style={{ gap: 0, minWidth: 0 }}>
          <div className="hstack panel-head" style={{ gap: 8 }}>
            <h2 className="h2 grow">{t("shipments.items")}</h2>
            <Button size="compact-sm" variant="default" disabled={!canEdit} onClick={() => setPicking("plot")}>
              {t("shipments.fromTrial")}
            </Button>
            <Button size="compact-sm" variant="default" disabled={!canEdit} onClick={() => setPicking("accession")}>
              {t("shipments.fromGenebank")}
            </Button>
            <Button size="compact-sm" variant="default" disabled={!canEdit} onClick={() => setPicking("query")}>
              {t("shipments.fromQuery")}
            </Button>
            <ShownOrderButton table={table} disabled={!canEdit} onOrder={() => order.keepShown(table.rows)} />
          </div>
          <DataTable
            table={table}
            rowKey={(x) => `${x.it.kind}:${refOf(x.it)}`}
            className="grow"
            about={about}
            rowProps={(x) => {
              const drop = order.rowProps(x);
              return { ...drop, className: [drop.className, x.planned?.checked.skipped && "mu"].filter(Boolean).join(" ") || undefined };
            }}
            empty={t("shipments.empty")}
          />
          <div className="inset">
            <RunBar
              requirements={[
                { label: t("shipments.needPartner"), done: draft.into != null || draft.caz != null },
                { label: t("shipments.needDate"), done: draft.into != null || !!draft.day },
                { label: t("shipments.needItem"), done: draft.items.length > 0 },
                { label: t("shipments.needAmount", { n: unweighed }), done: unweighed === 0 },
                { label: t("shipments.needValidItems"), done: !refused },
                { label: t("shipments.needPermission"), done: canEdit },
              ]}
              summary={plan && t("shipments.total", { n: kept.length, kg: formatNumber(plan.kg, 3), bags: plan.bags })}
              undoable
              busy={preview.busy || !plan || !kept.length}
              run={{
                label: draft.into ? t("shipments.addRecord", { n: kept.length }) : t("shipments.record", { n: kept.length }),
                loading: run.isPending || addRun.isPending,
                onClick: onRun,
              }}
            >
              <Checkbox
                size="xs"
                label={t("shipments.skipAlreadySent")}
                checked={draft.skip_duplicates}
                onChange={(e) => upd({ skip_duplicates: e.currentTarget.checked })}
              />
            </RunBar>
          </div>
        </section>
        <Splitter cssVar="--gb-docs-w" side="end" min={180} max={560} />
        <aside className="card pad stack gb-side gb-docs">
          <h2 className="h2">{t("shipments.documents")}</h2>
          {recorded?.id != null ? (
            <ResultBanner
              operation={recorded.operation}
              details={<Documents shipment={recorded.id} onChoose={() => setHistory(recorded.id)} />}
            >
              <b>
                {recorded.added
                  ? t("shipments.added", { id: recorded.id, n: recorded.written })
                  : t("shipments.recorded", { id: recorded.id, n: recorded.written })}
              </b>
            </ResultBanner>
          ) : (
            <p className="sm mu">{t("shipments.documentsAfterRecord")}</p>
          )}
        </aside>
      </div>
      {picking === "plot" && season && (
        <PlotPicker season={season} reps={draft.reps} onReps={(reps) => upd({ reps })} onAdd={add} onClose={() => setPicking(null)} />
      )}
      {picking === "accession" && <AccessionPicker onAdd={add} onClose={() => setPicking(null)} />}
      {picking === "query" && (
        <QueryPicker
          query={draft.query}
          onQuery={(query) => upd({ query })}
          reps={draft.reps}
          onReps={(reps) => upd({ reps })}
          onAdd={add}
          onClose={() => setPicking(null)}
        />
      )}
      {history !== undefined && (
        <HistoryModal
          start={history}
          onClose={() => setHistory(undefined)}
          onAddItems={
            canEdit
              ? (d) => {
                  upd({ into: { id: d.row.id, institution: d.recipient?.institution ?? null, day: d.row.day ?? null, remark: d.remark } });
                  setHistory(undefined);
                  // the window closes onto the page: say what changed, and what happens to items already in the list
                  notifications.show({
                    color: "blue",
                    message: draft.items.length
                      ? t("shipments.addModeOnWithItems", { id: d.row.id, n: draft.items.length })
                      : t("shipments.addModeOn", { id: d.row.id }),
                  });
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

function Recipient({
  draft,
  view,
  upd,
  chemicals,
}: {
  draft: Draft;
  view: RecipientView | undefined;
  upd: (p: Partial<Draft>) => void;
  chemicals: string[];
}) {
  const { t } = useTranslation();
  const r = view?.recipient;
  return (
    <section className="card pad stack gb-side gb-recipient">
      <span className="sec">{t("shipments.recipient")}</span>
      {draft.into ? (
        <div className="sm stack" style={{ gap: 6 }}>
          <b>
            № {draft.into.id} · {draft.into.institution ?? "—"}
          </b>
          <span className="mu">{formatDate(draft.into.day)}</span>
          {draft.into.remark && <span>{draft.into.remark}</span>}
          <span className="mu">{t("shipments.addToHelp")}</span>
          <Button size="compact-xs" variant="default" onClick={() => upd({ into: null })}>
            {t("shipments.newShipment")}
          </Button>
        </div>
      ) : (
        <>
          <PartnerSelect value={draft.caz} onChange={(caz) => upd({ caz })} />
          {r && (
            <div className="sm stack" style={{ gap: 2 }}>
              <b>{r.institution}</b>
              <span>{r.contact}</span>
              <span className="mu">
                {r.city} · {r.country}
              </span>
              {!r.active && <span className="chip r">{t("shipments.inactive")}</span>}
              {!!r.missing?.length && (
                <span className="note warn">
                  {t("shipments.partnerMissing", { fields: (r.missing ?? []).map((f) => t(`partners.field.${f}`)).join(", ") })}
                </span>
              )}
            </div>
          )}
          <Select
            size="xs"
            label={t("shipments.mailType")}
            placeholder={t("shipments.mailTypeChoose")}
            clearable
            allowDeselect={false}
            value={draft.mail_type}
            onChange={(v) => upd({ mail_type: v as ShipmentInMailType })}
            data={MAIL_TYPES.map((v) => ({ value: v as string, label: mailTypeText(t, v) ?? "" }))}
          />
          <DateField
            size="xs"
            clearable={false}
            label={t("shipments.date")}
            value={draft.day}
            onChange={(day) => upd({ day, day_chosen: true })}
          />
          <TextInput
            size="xs"
            label={t("shipments.shipmentRemark")}
            description={t("shipments.shipmentRemarkHelp")}
            value={draft.remark}
            onChange={(e) => upd({ remark: e.currentTarget.value })}
          />
        </>
      )}
      <span className="sec">{t("shipments.forNewItems")}</span>
      <Autocomplete
        size="xs"
        label={t("shipments.seedTreatment")}
        description={t("shipments.forNewItemsHelp")}
        value={draft.chemical}
        onChange={(chemical) => upd({ chemical })}
        data={chemicals}
      />
      {!draft.into && view && view.earlier.length > 0 && (
        <div className="stack" style={{ gap: 4 }}>
          <span className="sec">{t("shipments.previouslySent")}</span>
          {view.earlier.slice(0, 8).map((x) => (
            <span key={`${x.shipment}-${x.name}-${x.kg}`} className="sm hstack" style={{ gap: 6 }}>
              <span className="grow">
                {x.name ?? "—"} · {formatNumber(x.kg)} kg
              </span>
              <span className="mu mono">{x.shipment}</span>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

const NONE: string[] = [];

/** A draft item as the table shows it: its place in the draft and the preview's check of it. */
type ItemView = { it: DraftItem; index: number; planned: PlannedItem | undefined };
const placeOf = (x: ItemView) => x.index;
const itemOf = (x: ItemView) => x.it;

/** The draft items' columns: the item with its checks, then its fields as inputs (the rows keep their identity when
 *  sorted). */
function useItemColumns(chemicals: string[], onChange: (i: number, p: Partial<DraftItem>) => void, onRemove: (i: number) => void) {
  const { t } = useTranslation();
  return useMemo<Column<ItemView>[]>(() => {
    const nameOf = ({ it, planned }: ItemView) => planned?.name ?? it.name ?? null;
    const sourceOf = ({ planned }: ItemView) => planned?.checked.item.source;
    return [
      { id: "n", header: "#", title: t("shipments.placeHelp"), value: (x) => x.index + 1, numeric: true, className: "mono" },
      {
        id: "item",
        header: t("shipments.genotypeSource"),
        value: nameOf,
        text: (x) => `${nameOf(x) ?? ""} ${x.it.label}`,
        cell: (x) => {
          const { it, index } = x;
          const c = x.planned?.checked;
          const source = sourceOf(x);
          const key = source?.paz != null && source.sid != null ? { paz: source.paz, sid: source.sid } : null;
          return (
            <div className="stack" style={{ gap: 3 }}>
              <span className="hstack" style={{ gap: 6 }}>
                <button
                  type="button"
                  className="chip"
                  aria-label={t("shipments.deleteItem", { source: it.label })}
                  onClick={() => onRemove(index)}
                >
                  ✕
                </button>
                {key ? <LineLink line={{ key, name: nameOf(x) }} /> : (it.name ?? "—")}
                <span className="mono sm mu">{it.label}</span>
              </span>
              <span className="chips">
                {c?.problem && <span className="chip r">{t(`shipments.problems.${c.problem}`)}</span>}
                {(c?.warnings ?? []).map((w) => (
                  <span key={w} className="chip y">
                    {t(`shipments.warnings.${w}`)}
                  </span>
                ))}
                {!!c?.sent_in?.length && (
                  <span className="chip y">{t("shipments.warnings.sent_before", { ids: c.sent_in.join(", ") })}</span>
                )}
                {c && !c.problem && !(c.warnings ?? []).includes("no_cold_reserve") && (
                  <span className="chip g">{t("shipments.coldReserve")}</span>
                )}
                {c?.skipped && <span className="chip">{t("shipments.skipped")}</span>}
                {c?.left != null && (
                  <span className="chip" title={t("shipments.leftAfterHelp")}>
                    {t("shipments.leftAfter", {
                      left: it.measure === "bags" ? `${formatNumber(c.left)} ${t("shipments.bag")}` : `${formatNumber(c.left, 2)} kg`,
                    })}
                  </span>
                )}
              </span>
            </div>
          );
        },
      },
      {
        // kg in the heading (as TKW's g); a cold store's bags say so in their cell
        id: "amount",
        header: t("shipments.quantityKg"),
        value: (x) => x.it.amount,
        numeric: true,
        cell: ({ it, index }) => (
          <NumberInput
            size="xs"
            w={64}
            hideControls
            aria-label={t("shipments.quantityKg")}
            allowDecimal={it.measure === "kg"}
            min={0}
            value={it.amount}
            onChange={(v) => onChange(index, { amount: num(v) ?? 0 })}
            rightSectionWidth={it.measure === "bags" ? 40 : undefined}
            rightSection={it.measure === "bags" ? <span className="sm mu">{t("shipments.bag")}</span> : undefined}
          />
        ),
      },
      {
        id: "origin",
        header: t("shipments.origin"),
        title: t("shipments.originHelp"),
        value: (x) => sourceOf(x)?.origin ?? null,
        cellClassName: () => "mono sm",
      },
      {
        id: "tkw",
        header: "TKW g",
        value: (x) => x.it.tkw ?? sourceOf(x)?.tkw,
        numeric: true,
        cell: (x) => {
          const source = sourceOf(x);
          return (
            <NumberInput
              size="xs"
              w={52}
              hideControls
              aria-label="TKW"
              title={source?.tkw != null ? t("shipments.fromSource") : undefined}
              placeholder={source?.tkw != null ? formatNumber(source.tkw, 1) : undefined}
              min={0}
              value={x.it.tkw ?? ""}
              onChange={(v) => onChange(x.index, { tkw: num(v) })}
            />
          );
        },
      },
      {
        id: "germ",
        header: t("shipments.germination"),
        value: (x) => x.it.germ ?? sourceOf(x)?.germ,
        numeric: true,
        cell: (x) => {
          const source = sourceOf(x);
          return (
            <NumberInput
              size="xs"
              w={44}
              hideControls
              aria-label={t("shipments.germination")}
              title={source?.germ != null ? t("shipments.fromSource") : undefined}
              placeholder={source?.germ != null ? formatNumber(source.germ, 0) : undefined}
              min={0}
              max={100}
              value={x.it.germ ?? ""}
              onChange={(v) => onChange(x.index, { germ: num(v) })}
            />
          );
        },
      },
      {
        id: "chemical",
        header: t("shipments.seedTreatment"),
        value: (x) => x.it.chemical,
        // treated = a chemical is named (no separate tick: the breeders, 2026-10-29)
        cell: ({ it, index }) => (
          <Autocomplete
            size="xs"
            w={90}
            aria-label={t("shipments.seedTreatment")}
            placeholder={t("shipments.noTreatment")}
            value={it.chemical ?? ""}
            onChange={(v) => onChange(index, { chemical: v || null })}
            data={chemicals}
          />
        ),
      },
      {
        id: "remark",
        header: t("shipments.remark"),
        value: (x) => x.it.remark,
        cell: ({ it, index }) => (
          <TextInput
            size="xs"
            w={100}
            aria-label={t("shipments.remark")}
            value={it.remark ?? ""}
            onChange={(e) => onChange(index, { remark: e.currentTarget.value || null })}
          />
        ),
      },
    ];
  }, [t, chemicals, onChange, onRemove]);
}

/** A shipment's documents; ``items``: the rows ticked in its window (their labels and stickers only); ``onChoose``:
 *  opens that window to tick them. */
function Documents({ shipment, items, onChoose }: { shipment: number; items?: number[]; onChoose?: () => void }) {
  const { t } = useTranslation();
  const chosen = items?.length ? { items } : undefined;
  const docs: { key: string; links: [string, string][] }[] = [
    { key: "internalList", links: [["", getShipmentsDocumentListUrl(shipment)]] },
    {
      key: "deliveryNote",
      links: [
        ["PDF", getShipmentsDocumentDeliveryUrl(shipment)],
        ["xlsx", getShipmentsDocumentDeliverySheetUrl(shipment)],
      ],
    },
    { key: "label", links: [["", getShipmentsDocumentNovexxUrl(shipment, chosen)]] },
    { key: "sticker", links: [["", getShipmentsDocumentStickersUrl(shipment, chosen)]] },
    { key: "address", links: [["PDF", getShipmentsDocumentAddressUrl(shipment)]] },
  ];
  return (
    <div className="stack" style={{ gap: 6 }}>
      {items && (
        <span className="sm note">{items.length ? t("shipments.selectedLabels", { n: items.length }) : t("shipments.allLabels")}</span>
      )}
      {onChoose && (
        <Button size="compact-xs" variant="default" onClick={onChoose}>
          {t("shipments.chooseLabelItems")}
        </Button>
      )}
      {docs.map((d) => (
        <div key={d.key} className="gb-doc">
          <b>{t(`shipments.docs.${d.key}`)}</b>
          <span className="sm mu">{t(`shipments.docs.${d.key}Help`)}</span>
          <span className="sm hstack" style={{ gap: 8 }}>
            {d.links.map(([format, href]) => (
              <a key={href} href={href}>
                {t("shipments.download", { id: shipment })}
                {format && ` · ${format}`}
              </a>
            ))}
          </span>
          {d.key === "label" && <SendToPrinter send={(printer) => shipmentsSendNovexx(shipment, { printer, items: chosen?.items })} />}
        </div>
      ))}
    </div>
  );
}

/** Trial plots for the shipment: a trial's plots at a site, or the season's sister plots of the lines in chosen trials
 *  (the legacy "Show Sisters"), with their seed left in the harvest repetitions chosen; each as the plot or as
 *  subplot items (the seed is the plot's). */
function PlotPicker({
  season,
  reps,
  onReps,
  onAdd,
  onClose,
}: {
  season: number;
  reps: number[];
  onReps: (reps: number[]) => void;
  onAdd: (items: DraftItem[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const home = useSettings()?.default_th ?? "MV";
  const [tab, setTab] = useState<"trial" | "sisters">("trial");
  const [typed, setEv] = useState(season);
  const [ev] = useDebouncedValue(typed, 400);
  const [kis, setKis] = useState<string | null>(null);
  const [site, setSite] = useState<string | undefined>(undefined);
  const { trials, trial, th } = useTrial(ev, kis ?? undefined, site);
  const plots = useGenebankPlots({ ev, kis: kis ?? "", th: th ?? home, reps }, { query: { enabled: tab === "trial" && !!kis } }).data;
  const [sisterTrials, setSisterTrials] = useState<string[]>([]);
  const [exclude, setExclude] = useState<string[]>([]);
  const [withSeed, setWithSeed] = useState(true);
  const sisterSites = [...new Set(trials.filter((o) => sisterTrials.includes(o.kis)).flatMap((o) => o.sites))];
  const sisterSite = site && sisterSites.includes(site) ? site : (sisterSites[0] ?? home);
  const sisters = useGenebankSisters(
    { ev, trials: sisterTrials, th: sisterSite, reps, exclude },
    { query: { enabled: tab === "sisters" && sisterTrials.length > 0 } },
  ).data;
  const listed = useMemo<PlotRow[]>(
    () => (tab === "trial" ? (plots ?? []) : (sisters?.rows ?? []).filter((s) => !withSeed || (s.plot.left ?? 0) > 0)),
    [tab, plots, sisters, withSeed],
  );
  const [picked, setPicked] = useState<string[]>([]);
  const [amount, setAmount] = useState<number | null>(0.5);
  const [all, setAll] = useState(false);
  const [alps, setAlps] = useState("");
  const sisterRows = tab === "sisters";
  const picks = usePicks({
    picks: picked,
    keyOf: pickedPlotKey,
    onPicks: setPicked,
    label: (p: PlotRow) => t("shipments.selectRow", { item: `${p.plot.kis} ${p.plot.parc}` }),
  });
  const own = useMemo<Column<PlotRow>[]>(() => {
    // the picker's plots are whole plots (no subplot); a sister's with its trial
    const plotText = (p: PickedPlot) => plotLabel(p.plot, sisterRows ? p.plot.kis : undefined);
    const columns: (Column<PlotRow> | false)[] = [
      sisterRows && {
        id: "from",
        header: t("shipments.from"),
        value: (p) => (p.origins ?? []).map(([kis, parc]) => `${kis} ${parc}`).join(", "),
        className: "mono sm",
      },
      {
        id: "plot",
        header: t("shipments.plot"),
        value: (p) => (sisterRows ? plotText(p) : p.plot.parc),
        text: plotText,
        numeric: !sisterRows,
        className: sisterRows ? "n mono" : "mono",
      },
      { id: "name", header: t("shipments.genotype"), value: (p) => p.name },
      {
        id: "left",
        header: t("shipments.remainder"),
        value: (p) => p.plot.left,
        text: (p) => (p.plot.left != null ? `${formatNumber(p.plot.left, 2)} kg` : "—"),
        numeric: true,
      },
      { id: "tkw", header: "TKW g", value: (p) => p.plot.tkw, text: (p) => formatNumber(p.plot.tkw, 1), numeric: true },
      {
        id: "germ",
        header: t("shipments.germination"),
        value: (p) => p.plot.germ,
        text: (p) => formatNumber(p.plot.germ, 0),
        numeric: true,
      },
      { id: "used", header: t("shipments.used"), value: (p) => (p.plot.targets ?? []).join(", "), className: "sm mono" },
    ];
    return columns.filter((c): c is Column<PlotRow> => !!c);
  }, [t, sisterRows]);
  const columns = useMemo(() => [...picks.columns, ...own], [picks.columns, own]);
  const table = useDataTable(`genebank.shipments.${tab === "trial" ? "plots" : "sisters"}`, columns, listed, {
    partial: tab === "sisters" && !!sisters?.truncated,
    versions: picks.versions,
  });
  // the ticked plots on show, in the order they were ticked
  const chosen = picks.chosen(table.rows);
  const choose = () => onAdd(plotItems(chosen, subplots(alps), amount ?? 0.5, all));
  const names = [...new Set([...exclude, ...(sisters?.rows ?? []).map((s) => s.name).filter((n): n is string => !!n)])];
  return (
    <Modal opened onClose={onClose} title={t("shipments.fromTrialTitle")} size="xl">
      <div className="stack" style={{ gap: 10 }}>
        <Tabs
          value={tab}
          onChange={(v) => {
            setTab((v as typeof tab) ?? "trial");
            setPicked([]);
          }}
        >
          <AnimatedTabsList>
            <Tabs.Tab value="trial">{t("shipments.tabs.trialPlots")}</Tabs.Tab>
            <Tabs.Tab value="sisters">{t("shipments.tabs.sisterPlots")}</Tabs.Tab>
          </AnimatedTabsList>
        </Tabs>
        <div className="hstack" style={{ gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <YearInput size="xs" label={t("shipments.year")} value={typed} onChange={(v) => setEv(num(v) ?? season)} />
          {tab === "trial" ? (
            <>
              <Select
                placeholder={hint("searchSelect")}
                size="xs"
                searchable
                className="grow"
                label={t("shipments.trial")}
                value={kis}
                onChange={(v) => {
                  setKis(v);
                  setSite(undefined);
                }}
                data={trials.map((o) => o.kis)}
              />
              <Select
                size="xs"
                w={100}
                label={t("shipments.site")}
                value={th ?? null}
                onChange={(v) => setSite(v ?? undefined)}
                data={trial?.sites ?? []}
                disabled={!trial}
              />
            </>
          ) : (
            <>
              <MultiSelect
                size="xs"
                className="grow"
                searchable
                clearable
                label={t("shipments.sisterTrials")}
                placeholder={hint("searchSelect")}
                value={sisterTrials}
                onChange={setSisterTrials}
                data={trials.map((o) => o.kis)}
              />
              <Select
                size="xs"
                w={100}
                label={t("shipments.site")}
                value={sisterSite}
                onChange={(v) => setSite(v ?? undefined)}
                data={sisterSites.length ? sisterSites : [home]}
              />
            </>
          )}
          <div className="stack" style={{ gap: 4 }}>
            <span className="sm">{t("shipments.yieldReps")}</span>
            <Chip.Group multiple value={reps.map(String)} onChange={(v) => v.length && onReps(v.map(Number).sort())}>
              <span className="hstack" style={{ gap: 4 }} title={t("shipments.yieldRepsHelp")}>
                {REPS.map((r) => (
                  <Chip key={r} size="xs" value={String(r)}>
                    {ROMAN[r - 1]}
                  </Chip>
                ))}
              </span>
            </Chip.Group>
          </div>
        </div>
        {tab === "sisters" && (
          <div className="hstack" style={{ gap: 8, alignItems: "flex-end" }}>
            <MultiSelect
              size="xs"
              className="grow"
              searchable
              clearable
              label={t("shipments.sisterExcluded")}
              placeholder={hint("searchSelect")}
              value={exclude}
              onChange={setExclude}
              data={names}
            />
            <Checkbox
              size="xs"
              label={t("shipments.onlyWithRemainder")}
              checked={withSeed}
              onChange={(e) => setWithSeed(e.currentTarget.checked)}
            />
          </div>
        )}
        {tab === "sisters" && <p className="sm mu">{t("shipments.sistersHelp")}</p>}
        <div className="stack" style={{ maxHeight: 380, minHeight: 0 }}>
          <DataTable table={table} rowKey={(p) => plotKey(p.plot)} rowProps={(p) => picks.rowProps(p)} />
          {tab === "sisters" && sisters?.truncated && <p className="sm mu">{t("shipments.truncated", { n: sisters.rows.length })}</p>}
        </div>
        <div className="hstack" style={{ gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <NumberInput
            size="xs"
            w={110}
            label={t("shipments.kgPerItem")}
            min={0}
            disabled={all}
            value={amount ?? ""}
            onChange={(v) => setAmount(num(v))}
          />
          <Checkbox size="xs" label={t("shipments.wholeRemainder")} checked={all} onChange={(e) => setAll(e.currentTarget.checked)} />
          <TextInput
            size="xs"
            w={200}
            label={t("shipments.subplots")}
            placeholder={t("shipments.subplotsHelp")}
            value={alps}
            onChange={(e) => setAlps(e.currentTarget.value)}
          />
          <Button className="grow" disabled={!chosen.length} onClick={choose}>
            {t("shipments.addSelected", { n: chosen.length * subplots(alps).length })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AccessionPicker({ onAdd, onClose }: { onAdd: (items: DraftItem[]) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const containers = useGenebankContainers().data ?? [];
  const [container, setContainer] = useState<string | null>("HUTO");
  const [q, setQ] = useState("");
  const found = useGenebankAccessions({ container: container ?? undefined, q }, { query: { enabled: !!container } }).data;
  const bags = containers.find((c) => c.name === container)?.kind === "bags";
  const [picked, setPicked] = useState<number[]>([]);
  const picks = usePicks({
    picks: picked,
    keyOf: accessionId,
    onPicks: setPicked,
    label: (a: Accession) => t("shipments.selectRow", { item: `${a.kis} ${a.slot}` }),
  });
  const own = useMemo<Column<Accession>[]>(
    () => [
      { id: "location", header: t("shipments.location"), value: (a) => `${a.kis} ${a.slot ?? ""}`, className: "mono" },
      { id: "name", header: t("shipments.genotype"), value: (a) => a.name },
      bags
        ? { id: "amount", header: t("shipments.bag"), value: (a) => a.bags, text: (a) => cellText(a.bags), numeric: true }
        : { ...kgColumn<Accession>((a) => a.kg), id: "amount" },
    ],
    [t, bags],
  );
  const columns = useMemo(() => [...picks.columns, ...own], [picks.columns, own]);
  // a container's accessions up to the server's limit (``truncated``: there are more)
  const table = useDataTable("genebank.shipments.accessions", columns, found?.rows, {
    partial: !!found?.truncated,
    versions: picks.versions,
  });
  // the ticked accessions on show, in the order they were ticked
  const chosen = picks.chosen(table.rows);
  const choose = () =>
    onAdd(
      chosen.map((a) => ({
        kind: "accession" as const,
        id: a.id,
        amount: bags ? 1 : Math.min(0.1, a.kg ?? 0.1),
        label: `${a.kis} ${a.slot ?? ""}`,
        name: a.name,
        measure: bags ? ("bags" as const) : ("kg" as const),
        remark: a.origin ?? null, // where the seed came from (the breeders: EKis / EParc and its year)
      })),
    );
  return (
    <Modal opened onClose={onClose} title={t("shipments.fromGenebankTitle")} size="lg">
      <div className="stack" style={{ gap: 10 }}>
        <div className="hstack" style={{ gap: 8 }}>
          <Select
            size="xs"
            w={160}
            label={t("shipments.storage")}
            value={container}
            onChange={setContainer}
            data={containers.map((c) => c.name)}
          />
          <SearchBox label={t("shipments.search")} value={q} onChange={setQ} />
        </div>
        <div className="stack" style={{ maxHeight: 380, minHeight: 0 }}>
          <DataTable table={table} rowKey={(a) => a.id} rowProps={(a) => picks.rowProps(a)} />
        </div>
        <Button disabled={!chosen.length} onClick={choose}>
          {t("shipments.addSelected", { n: chosen.length })}
        </Button>
      </div>
    </Modal>
  );
}

/** Items from a saved query (the legacy Magk with the Query closed on it): a plot query's plots (a subplot row an item
 *  of the subplot, the seed the plot's; a register row the plot at the site chosen), a gene-bank query's accessions,
 *  in the query's order, each with what it has left; the amounts are set here, and in the table after. */
function QueryPicker(props: {
  query: number | null;
  onQuery: (query: number | null) => void;
  reps: number[];
  onReps: (reps: number[]) => void;
  onAdd: (items: DraftItem[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const saved = useSavedQuery(props.query);
  const home = useSettings()?.default_th ?? "MV";
  const [site, setSite] = useState(home);
  const [th] = useDebouncedValue(site.trim().toUpperCase() || home, 400);
  const params = { query: props.query ?? 0, th, reps: props.reps };
  // saving the query in the builder reads it again (its updated_at is in the key)
  const sources = useShipmentsQuerySources(params, {
    query: { enabled: props.query != null && saved.ready, queryKey: [...getShipmentsQuerySourcesQueryKey(params), saved.stamp] },
  });
  const found = sources.data;
  const rows = found?.rows ?? [];
  // every source with seed left is taken, in the query's order, until the ticks are changed; then in the order ticked
  // (one with nothing left only when ticked)
  const [ticks, setTicks] = useState<string[] | null>(null);
  const ticked = useMemo(() => ticks ?? rows.filter(hasSeed).map(sourceRef), [ticks, rows]);
  const picks = usePicks({
    picks: ticked,
    keyOf: sourceRef,
    onPicks: setTicks,
    label: (r: QuerySource) => t("shipments.selectRow", { item: r.source.label }),
    all: hasSeed,
  });
  const [amount, setAmount] = useState<number | null>(0.5);
  const [all, setAll] = useState(false);
  const bags = found?.kind === "accession" && rows.every((r) => r.source.measure === "bags");
  const kind = found?.kind;
  const own = useMemo<Column<QuerySource>[]>(() => {
    const have = ({ source }: QuerySource) =>
      source.have == null
        ? "—"
        : source.measure === "bags"
          ? `${formatNumber(source.have)} ${t("shipments.bag")}`
          : `${formatNumber(source.have, 2)} kg`;
    return [
      {
        id: "source",
        header: kind === "accession" ? t("shipments.location") : t("shipments.plot"),
        value: (r) => r.source.label,
        className: "mono sm",
      },
      { id: "name", header: t("shipments.genotype"), value: (r) => r.name },
      {
        id: "have",
        header: t("shipments.remainder"),
        value: (r) => r.source.have,
        text: have,
        numeric: true,
        cellClassName: (r) => (isEmptySource(r) ? "warn" : undefined),
      },
      { id: "tkw", header: "TKW g", value: (r) => r.source.tkw, text: (r) => formatNumber(r.source.tkw, 1), numeric: true },
      {
        id: "germ",
        header: t("shipments.germination"),
        value: (r) => r.source.germ,
        text: (r) => formatNumber(r.source.germ, 0),
        numeric: true,
      },
    ];
  }, [t, kind]);
  const columns = useMemo(() => [...picks.columns, ...own], [picks.columns, own]);
  // the query's order is the page's own; ``cut``: only its first sources are listed
  const table = useDataTable("genebank.shipments.query", columns, found?.rows, { partial: !!found?.cut, versions: picks.versions });
  // the sources on show that are taken, in the order ticked
  const taken = picks.chosen(table.rows);
  return (
    <Modal opened onClose={props.onClose} title={t("shipments.fromQueryTitle")} size="xl">
      <div className="stack" style={{ gap: 10 }}>
        <div className="hstack" style={{ gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <SavedQuerySelect
            value={props.query}
            onChange={(id) => {
              setTicks(null);
              props.onQuery(id);
            }}
            w={280}
            required
            description={t("shipments.queryHelp")}
          />
          <TextInput
            size="xs"
            w={90}
            label={t("shipments.site")}
            description={t("shipments.querySiteHelp")}
            value={site}
            onChange={(e) => setSite(e.currentTarget.value)}
          />
          <div className="stack" style={{ gap: 4 }}>
            <span className="sm">{t("shipments.yieldReps")}</span>
            <Chip.Group multiple value={props.reps.map(String)} onChange={(v) => v.length && props.onReps(v.map(Number).sort())}>
              <span className="hstack" style={{ gap: 4 }} title={t("shipments.yieldRepsHelp")}>
                {REPS.map((r) => (
                  <Chip key={r} size="xs" value={String(r)}>
                    {ROMAN[r - 1]}
                  </Chip>
                ))}
              </span>
            </Chip.Group>
          </div>
        </div>
        <QueryFeedback busy={sources.isFetching} error={sources.error} retry={sources.refetch} label={t("async.loading")} />
        {found && (
          <div className="stack" style={{ gap: 2 }}>
            <span className="sm mu">
              {t("shipments.queryResult", {
                n: formatNumber(rows.length),
                kind: t(`shipments.queryKind.${found.kind}`),
                rows: formatNumber(found.query_rows),
              })}
              {found.missing > 0 && ` · ${t("shipments.queryMissing", { n: formatNumber(found.missing) })}`}
            </span>
            {found.query_truncated && (
              <span className="sm note warn">{t("shipments.queryTruncated", { n: formatNumber(found.query_rows) })}</span>
            )}
            {found.cut && <span className="sm note warn">{t("shipments.queryCapped")}</span>}
          </div>
        )}
        <div className="stack" style={{ maxHeight: 380, minHeight: 0 }}>
          <DataTable table={table} rowKey={(r) => r.source.ref} rowProps={(r) => picks.rowProps(r)} />
        </div>
        <div className="hstack" style={{ gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          {!bags && (
            <NumberInput
              size="xs"
              w={110}
              label={t("shipments.kgPerItem")}
              description={found?.kind === "accession" ? undefined : t("shipments.queryBags")}
              min={0}
              disabled={all}
              value={amount ?? ""}
              onChange={(v) => setAmount(num(v))}
            />
          )}
          <Checkbox size="xs" label={t("shipments.wholeStock")} checked={all} onChange={(e) => setAll(e.currentTarget.checked)} />
          <Button className="grow" disabled={!taken.length} onClick={() => props.onAdd(queryItems(taken, amount ?? 0.5, all))}>
            {t("shipments.addSelected", { n: taken.length })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function HistoryModal({
  start,
  onClose,
  onAddItems,
}: {
  /** the shipment shown first (null: the list) */
  start: number | null;
  onClose: () => void;
  onAddItems?: (detail: ShipmentDetail) => void;
}) {
  const { t } = useTranslation();
  const list = useShipmentsShipments().data;
  const [open, setOpen] = useState<number | null>(start);
  const columns = useMemo<Column<ShipmentRow>[]>(() => {
    const recipient = (s: ShipmentRow) => [s.institution, s.contact].filter(Boolean).join(" · ");
    return [
      { id: "id", header: "№", value: (s) => s.id, text: (s) => String(s.id), numeric: true, className: "mono" },
      { id: "day", header: t("shipments.date"), value: (s) => s.day, text: (s) => formatDate(s.day) },
      { id: "recipient", header: t("shipments.recipient"), value: recipient },
      { id: "items", header: t("shipments.item"), value: (s) => s.items, numeric: true },
      kgColumn((s) => s.kg),
      // blank: the dispatch date was not recorded yet (sort or filter to find them, the legacy "ellenorkuld" check)
      { id: "shipped", header: t("shipments.dispatched"), value: (s) => s.shipped, text: (s) => formatDate(s.shipped) },
      { id: "arrived", header: t("shipments.arrived"), value: (s) => s.arrived, text: (s) => formatDate(s.arrived) },
    ];
  }, [t]);
  // the newest shipments only: a full list may have older ones
  const table = useDataTable("genebank.shipments.history", columns, list, { partial: (list?.length ?? 0) >= LISTED });
  return (
    <Modal opened onClose={onClose} title={open != null ? t("shipments.shipmentTitle", { id: open }) : t("shipments.previous")} size="xl">
      {open != null ? (
        <ShipmentView id={open} onBack={() => setOpen(null)} onAddItems={onAddItems} />
      ) : (
        <div className="stack" style={{ maxHeight: 520, minHeight: 0 }}>
          <DataTable table={table} rowKey={(s) => s.id} rowProps={(s) => ({ className: "click", onClick: () => setOpen(s.id) })} />
        </div>
      )}
    </Modal>
  );
}

function ShipmentView({ id, onBack, onAddItems }: { id: number; onBack: () => void; onAddItems?: (detail: ShipmentDetail) => void }) {
  const { t } = useTranslation();
  const detail = useShipmentsDetail(id).data;
  const canEdit = hasRole(useUserMe().data?.roles, "genbank");
  const changed = useInvalidate(/^\/api\/v1\/(shipments|arrivals|genebank|years|measurements|audit)/);
  const [picked, setPicked] = useState<number[]>([]);
  const picks = usePicks({
    picks: picked,
    keyOf: shippedId,
    onPicks: setPicked,
    label: (it: ShippedItem) => t("shipments.selectItem", { item: it.line }),
  });
  const { mutate: removeItem, isPending: removing } = useShipmentsRemoveItem({
    mutation: {
      // the shipment stays on show, one item shorter (it does not jump back to the list)
      onSuccess: () => {
        changed();
        setPicked([]);
        notifications.show({ color: "green", message: t("shipments.itemRemoved", { id }) });
      },
    },
  });
  const own = useMemo<Column<ShippedItem>[]>(() => {
    const treatment = (it: ShippedItem) => it.chemical || (it.treated ? t("value.yes") : t("value.no"));
    return [
      { id: "line", header: "#", value: (it) => it.line, text: (it) => String(it.line), numeric: true },
      { id: "source", header: t("shipments.source"), value: (it) => it.source, className: "mono sm" },
      { id: "name", header: t("shipments.genotype"), value: (it) => it.name },
      {
        id: "origin",
        header: t("shipments.origin"),
        title: t("shipments.originHelp"),
        value: (it) => it.origin,
        cellClassName: () => "mono sm",
      },
      kgColumn((it) => it.kg),
      { id: "chemical", header: t("shipments.seedTreatment"), value: treatment, className: "sm" },
      { id: "remark", header: t("shipments.remark"), value: (it) => it.remark, className: "sm" },
      removeColumn({ canEdit, busy: removing, onRemove: (it) => removeItem({ shipment: id, id: it.id, params: { token: it.token } }) }),
    ];
  }, [t, canEdit, removing, removeItem, id]);
  const columns = useMemo(() => [...picks.columns, ...own], [picks.columns, own]);
  const table = useDataTable("genebank.shipments.shipment-items", columns, detail?.items, { versions: picks.versions });
  if (!detail) return <Loader size="sm" />;
  // the ticked items on show, in the order ticked: their labels and stickers in that order
  const chosen = picks.chosen(table.rows).map((it) => it.id);
  return (
    <div className="stack" style={{ gap: 12 }}>
      {/* the buttons on a row of their own, whole; the shipment's date and recipient (the number is in the window's
          title) under them, wrapping when the institution's name is long */}
      <div className="hstack" style={{ gap: 8, justifyContent: "space-between", flexWrap: "wrap" }}>
        <BackButton size="compact-sm" onClick={onBack}>
          {t("shipments.back")}
        </BackButton>
        {onAddItems && (
          <Tooltip label={t("shipments.addItemsHelp")} multiline w={280}>
            <Button
              size="compact-sm"
              style={{ flexShrink: 0 }}
              leftSection={<Icon name="plus" size={14} />}
              onClick={() => onAddItems(detail)}
            >
              {t("shipments.addItems")}
            </Button>
          </Tooltip>
        )}
      </div>
      <div className="stack" style={{ gap: 2 }}>
        <b style={{ overflowWrap: "anywhere" }}>{detail.recipient?.institution ?? "—"}</b>
        <span className="sm mu">{formatDate(detail.row.day)}</span>
      </div>
      <DataTable
        table={table}
        rowKey={(it) => it.line}
        rowProps={(it) => picks.rowProps(it)}
        about={shipmentLines(
          t,
          { ...detail.row, institution: partnerLine(detail.recipient) ?? detail.row.institution, remark: detail.remark },
          [
            [t("shipments.mailType"), mailTypeText(t, detail.mail_type)],
            [t("shipments.dispatched"), detail.shipped && formatDate(detail.shipped)],
            [t("shipments.arrived"), detail.row.arrived && formatDate(detail.row.arrived)],
            [t("shipments.parcelNo"), detail.row.parcel_no],
            [t("shipments.flightNo"), detail.flight_no],
          ],
        )}
      />
      <Documents shipment={detail.row.id} items={chosen} />
      {canEdit && (
        <DetailsForm
          id={id}
          initial={{
            arrived: detail.row.arrived,
            shipped: detail.shipped,
            parcel_no: detail.row.parcel_no,
            flight_no: detail.flight_no,
            remark: detail.remark,
          }}
        />
      )}
    </div>
  );
}

type DetailsDraft = {
  arrived?: string | null;
  shipped?: string | null;
  parcel_no?: string | null;
  flight_no?: string | null;
  remark?: string | null;
};

/** The details added later (the legacy Complementary Data), saved as a whole: a field left empty is cleared. */
function DetailsForm({ id, initial }: { id: number; initial: DetailsDraft }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [form, setForm] = useState(initial);
  const set = (p: DetailsDraft) => setForm((f) => ({ ...f, ...p }));
  const save = useShipmentsDetails({
    mutation: {
      onSuccess: () => {
        notifications.show({ color: "green", message: t("shipments.extraSaved") });
        void client.invalidateQueries({ queryKey: [getShipmentsShipmentsQueryKey()[0]] });
        void client.invalidateQueries({ queryKey: getShipmentsDetailQueryKey(id) });
      },
    },
  });
  const field = (key: keyof DetailsDraft, label: string, type?: string) => (
    <TextInput size="xs" type={type} label={label} value={form[key] ?? ""} onChange={(e) => set({ [key]: e.currentTarget.value })} />
  );
  return (
    <div className="stack" style={{ gap: 8 }}>
      <span className="sec">{t("shipments.extraData")}</span>
      <div className="hstack" style={{ gap: 8, alignItems: "flex-end" }}>
        {field("shipped", t("shipments.dispatched"), "date")}
        {field("arrived", t("shipments.arrived"), "date")}
        {field("parcel_no", t("shipments.parcelNo"))}
        {field("flight_no", t("shipments.flightNo"))}
        {field("remark", t("shipments.shipmentRemark"))}
        <Button
          size="xs"
          loading={save.isPending}
          onClick={() =>
            save.mutate({
              shipment: id,
              data: {
                arrived: form.arrived || null,
                shipped: form.shipped || null,
                parcel_no: form.parcel_no || null,
                flight_no: form.flight_no || null,
                remark: form.remark || null,
              },
            })
          }
        >
          {t("shipments.saveExtra")}
        </Button>
      </div>
    </div>
  );
}
